import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IGroupMemberCount, IGroupVaultCount, IGroupApplicationCount, IGroupListCount, IPeopleListCount, Order } from '@app/models';
import { IActiveFilter } from '@app/models';
import { emptyGuid, infiniteScrollItemsFetchLimit } from '@app/helpers';
import { manageSearchFilters } from './filters.service';

class GroupsService {
    readonly apiBase: string = '/api/directory/Groups';

    getGroups(
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        organizationId: string,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(this.apiBase, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters, organizationId) } : { $filter: manageFilters([], organizationId) })
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsForPolicies(top: number, skip: number, isReset: boolean, filters?: IActiveFilter[]): AxiosPromise {
        return apiService
            .get(this.apiBase, {
                params: {
                    $apply: 'groupby((Name,OrganizationId))',
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : { $filter: manageFilters([]) })
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupMemberCount(groupIds: any): AxiosPromise {
        const data = {
            Groups: groupIds
        };

        return apiService
            .post(`${this.apiBase}/memberscount`, data)
            .then((response: AxiosResponse<IGroupMemberCount>) => {
                return response;
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupListCount(groupIds: any): AxiosPromise {
        const data = {
            Groups: groupIds
        };

        return apiService
            .post(`${this.apiBase}/GroupListCount`, data)
            .then((response: AxiosResponse<IGroupListCount>) => {
                return response;
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getPeopleListCount(groupIds: any): AxiosPromise {
        const data = {
            Groups: groupIds
        };

        return apiService
            .post(`${this.apiBase}/PeopleListCount`, data)
            .then((response: AxiosResponse<IPeopleListCount>) => {
                return response;
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupVaultCount(groupIds: any): AxiosPromise {
        const data = {
            Groups: groupIds
        };

        return apiService
            .post(`${this.apiBase}/vaultscount`, data)
            .then((response: AxiosResponse<IGroupVaultCount>) => {
                return response;
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupApplicationCount(groupIds: any): AxiosPromise {
        const data = {
            Groups: groupIds
        };

        return apiService
            .post(`${this.apiBase}/applicationscount`, data)
            .then((response: AxiosResponse<IGroupApplicationCount>) => {
                return response;
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsDTO(orderBy: string, asc: string, organizationId: string, skip?: number, isReset?: boolean): AxiosPromise {
        return apiService
            .get(`${this.apiBase}/GroupsDTO`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: infiniteScrollItemsFetchLimit,
                    $skip: skip,
                    $count: isReset,
                    $filter: 'OrganizationId eq ' + organizationId
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsDTOInitial(
        orderBy: string,
        asc: string,
        organizationId: string,
        skip: number,
        handleError?: Function,
        handleSuccess?: Function
    ): AxiosPromise {
        return apiService
            .get(`${this.apiBase}/GroupsDTO`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $filter: 'OrganizationId eq ' + organizationId,
                    $top: 100,
                    $skip: skip,
                    $count: true
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    async getAllGroupsDTOInitial(asc: string, organizationId: string, skip: number = 0, values: any[] = []): Promise<any[]> {
        const resp: AxiosResponse = await this.getGroupsDTOInitial('name', asc, organizationId, skip);

        skip += resp.data.value.length;
        values = [...values, ...resp.data.value];

        if (skip < resp.data['@odata.count']) {
            return this.getAllGroupsDTOInitial(asc, organizationId, skip, values);
        } else {
            return values;
        }
    }

    getGroupsDTOFilter(
        orderBy: string,
        asc: string,
        organizationId: string,
        filterNameText: string,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        return apiService
            .get(`${this.apiBase}/GroupsDTO`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $filter: 'contains(name, \'' + filterNameText + '\') and OrganizationId eq ' + organizationId
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getUsersNotInGroup(
        groupId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Groups(${groupId})/UsersNotInGroup`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    addUserForGroup(groupId: string, userForGroup: any[]): AxiosPromise {
        const apiUrl: string = `/api/directory/Groups(${groupId})/AddPeople`;

        return apiService
            .post(apiUrl, {
                People: userForGroup.map((user) => user.id)
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsNotInGroup(
        groupId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Groups(${groupId})/GroupsNotInGroup`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    addGroupForGroup(groupId: string, groupForGroup: any[]): AxiosPromise {
        const apiUrl: string = `/api/directory/Groups(${groupId})/AddGroup`;

        return apiService
            .post(apiUrl, {
                Group: groupForGroup.map((group) => group.id)
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsInGroup(
        groupId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Groups(${groupId})/GroupsInGroup`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageSearchFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getUsersInGroup(
        groupId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/Groups(${groupId})/UsersInGroup`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageSearchFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    removeGroupsFromGroup(groupId: string, groupsForGroup: any[]): AxiosPromise {
        const apiUrl: string = `/api/directory/Groups(${groupId})/RemoveGroups`;

        return apiService
            .post(apiUrl, {
                Groups: groupsForGroup.map((group) => group.id)
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    removeUsersFromGroup(groupId: string, usersForGroup: any[]): AxiosPromise {
        const apiUrl: string = `/api/directory/Groups(${groupId})/RemovePeople`;

        return apiService
            .post(apiUrl, {
                People: usersForGroup.map((users) => users.id)
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    deleteGroups(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/DeleteGroups`;

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    detachGroups(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/DetachGroups`;

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    /******************************************************************
     ******************* APIs specific to one Group ********************
     *******************************************************************/
    getGroup(groupId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${groupId})?$expand=Attributes`;

        return apiService.get(apiUrl).catch((error: AxiosResponse) => Promise.reject(error));
    }

    getVaultsInGroup(groupId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${groupId})/VaultsInGroup`;

        return apiService.get(apiUrl).catch((error: AxiosResponse) => Promise.reject(error));
    }

    getApplicationsInGroup(groupId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${groupId})/ApplicationsInGroup`;

        return apiService.get(apiUrl).catch((error: AxiosResponse) => Promise.reject(error));
    }

    getPeopleCountInGroup(groupId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${groupId})/PeopleCount`;

        return apiService.get(apiUrl).catch((error: AxiosResponse) => Promise.reject(error));
    }

    getGroupsCountInGroup(groupId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${groupId})/GroupsCount`;

        return apiService.get(apiUrl).catch((error: AxiosResponse) => Promise.reject(error));
    }

    disconnectGroupFromDirsync(groupId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${groupId})?$expand=People,Groups`;

        return apiService
            .patch(
                apiUrl,
                {
                    SyncSource: emptyGuid
                },
                { onError: handleError, onSuccess: handleSuccess }
            )
            .catch();
    }

    addGroup(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = this.apiBase;

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    editGroup(groupId: string, jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + `(${groupId})`;

        return apiService.patch(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }
}

export const groupsService = new GroupsService();
